// spring-math.js — Federphysik für Material 3 Expressive (rein, läuft in Node und im Browser).
//
// M3 Expressive beschreibt Bewegung als gedämpfte Feder (Masse 1) mit Dämpfungsverhältnis ζ
// und Steifigkeit k (Werte aus androidx Compose tokens/ExpressiveMotionTokens.kt).
// CSS kennt keine Federn — daraus werden hier linear()-Kurven + Dauer erzeugt, und für
// Gesten (Sheet ziehen) eine schrittweise Simulation mit Startgeschwindigkeit.

/** Offizielle Expressive-Tokens: spatial (Position/Größe/Form, darf überschwingen), effects (Farbe/Deckkraft, nie). */
export const SPRINGS = {
  'spatial-fast':    { zeta: 0.6, stiffness: 800 },
  'spatial-default': { zeta: 0.8, stiffness: 380 },
  'spatial-slow':    { zeta: 0.8, stiffness: 200 },
  'effects-fast':    { zeta: 1.0, stiffness: 3800 },
  'effects-default': { zeta: 1.0, stiffness: 1600 },
  'effects-slow':    { zeta: 1.0, stiffness: 800 },
};

/**
 * Sprungantwort von 0 nach 1 zum Zeitpunkt t (Sekunden), Startgeschwindigkeit v0 (Einheiten/s,
 * bezogen auf die Strecke 0→1). Analytisch für unter-, kritisch und überdämpft.
 */
export function springValue(zeta, stiffness, t, v0 = 0) {
  const w = Math.sqrt(stiffness);
  const x0 = -1; // Auslenkung relativ zum Ziel
  if (zeta < 1) {
    const wd = w * Math.sqrt(1 - zeta * zeta);
    const a = x0, b = (v0 + zeta * w * x0) / wd;
    return 1 + Math.exp(-zeta * w * t) * (a * Math.cos(wd * t) + b * Math.sin(wd * t));
  }
  if (zeta === 1) {
    return 1 + Math.exp(-w * t) * (x0 + (v0 + w * x0) * t);
  }
  const r = Math.sqrt(zeta * zeta - 1);
  const s1 = -w * (zeta - r), s2 = -w * (zeta + r);
  const c2 = (v0 - s1 * x0) / (s2 - s1), c1 = x0 - c2;
  return 1 + c1 * Math.exp(s1 * t) + c2 * Math.exp(s2 * t);
}

/** Einschwingzeit (s): ab hier bleibt |x−1| < tol. */
export function settleTime(zeta, stiffness, tol = 0.001, v0 = 0) {
  const dt = 0.0005;
  let last = 0;
  for (let t = 0; t < 5; t += dt) {
    if (Math.abs(springValue(zeta, stiffness, t, v0) - 1) >= tol) last = t;
  }
  return last + dt;
}

/** Ramer–Douglas–Peucker auf [t, x]-Punkten (t normiert 0..1). */
function simplify(points, eps) {
  if (points.length < 3) return points;
  const [a, b] = [points[0], points[points.length - 1]];
  let maxD = 0, idx = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i];
    // senkrechter Abstand in x-Richtung zur Sehne (relevant ist der Wertfehler)
    const tt = (p[0] - a[0]) / (b[0] - a[0] || 1);
    const d = Math.abs(p[1] - (a[1] + tt * (b[1] - a[1])));
    if (d > maxD) { maxD = d; idx = i; }
  }
  if (maxD <= eps) return [a, b];
  const left = simplify(points.slice(0, idx + 1), eps);
  const right = simplify(points.slice(idx), eps);
  return left.slice(0, -1).concat(right);
}

/**
 * Feder → { durationMs, points: [[tNorm, x], …] } mit höchstens maxPoints Stützpunkten
 * und einem maximalen Wertfehler eps.
 */
export function springCurve(zeta, stiffness, { tol = 0.001, eps = 0.004, samples = 600, maxPoints = 60 } = {}) {
  const T = settleTime(zeta, stiffness, tol);
  const raw = [];
  for (let i = 0; i <= samples; i++) {
    const tn = i / samples;
    raw.push([tn, i === samples ? 1 : springValue(zeta, stiffness, tn * T)]);
  }
  let e = eps, pts = simplify(raw, e);
  while (pts.length > maxPoints) { e *= 1.25; pts = simplify(raw, e); }
  return { durationMs: Math.round(T * 1000), points: pts };
}

const r4 = (n) => Math.round(n * 10000) / 10000;
const pct = (n) => Math.round(n * 1000) / 10;

/** CSS linear()-Funktion aus Stützpunkten. */
export function linearCss(points) {
  const parts = points.map(([t, x], i) => {
    if (i === 0) return String(r4(x));
    if (i === points.length - 1) return String(r4(x));
    return r4(x) + ' ' + pct(t) + '%';
  });
  return 'linear(' + parts.join(', ') + ')';
}

/** Größte Überschwingweite (über 1) — für Tests und Doku. */
export function overshoot(zeta, stiffness) {
  const T = settleTime(zeta, stiffness);
  let max = 1;
  for (let t = 0; t <= T; t += 0.0005) max = Math.max(max, springValue(zeta, stiffness, t));
  return max - 1;
}

/**
 * Schrittweise Simulation für Gesten (rAF): halbimpliziter Euler, Masse 1.
 * state = { x, v } in beliebigen Einheiten, target = Zielwert.
 */
export function springStep(state, target, zeta, stiffness, dt) {
  const w = Math.sqrt(stiffness);
  const c = 2 * zeta * w;
  // Unterschritte für Stabilität bei großem dt
  const steps = Math.max(1, Math.ceil(dt / 0.004));
  const h = dt / steps;
  let { x, v } = state;
  for (let i = 0; i < steps; i++) {
    const a = -stiffness * (x - target) - c * v;
    v += a * h;
    x += v * h;
  }
  return { x, v };
}

/** Ruht die Feder (für das Beenden einer rAF-Schleife)? */
export function springAtRest(state, target, eps = 0.5, vEps = 5) {
  return Math.abs(state.x - target) < eps && Math.abs(state.v) < vEps;
}
